import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Shield } from "lucide-react";
import { safeFetchJson } from "@/utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check URL path for admin login
  const isAdminRoute = location.pathname === "/admin/login" || location.pathname === "/admin";
  const role = isAdminRoute ? "admin" : (location.state?.role || "trainer");
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Default to true for convenience
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use dedicated admin login endpoint for rate-limited admin authentication
      const loginEndpoint = isAdminRoute ? `${API_URL}/api/auth/admin/login` : `${API_URL}/api/auth/login`;
      
      // Use safeFetchJson which handles response body reading safely (reads ONCE only)
      const parsed = await safeFetchJson(loginEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, remember_me: rememberMe })
      });

      if (!parsed.ok) {
        // Handle specific HTTP status codes
        if (parsed.status === 429) {
          throw new Error(parsed.data?.detail || "Too many login attempts. Please try again later.");
        }
        if (parsed.status === 401) {
          throw new Error(parsed.data?.detail || "Invalid email or password. If you forgot your password, please contact support for assistance.");
        }
        if (parsed.status === 403) {
          throw new Error(parsed.data?.detail || "Your account has been suspended. Please contact support for assistance.");
        }
        throw new Error(parsed.data?.detail || parsed.data?.message || parsed.raw || "Login failed. Please contact support if the issue persists.");
      }

      const data = parsed.data;
      if (!data || !data.token) {
        throw new Error("Invalid response from server");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      
      // Debug logging
      console.log("[AUTH] Login success:", {
        email: data.user.email,
        role: data.user.role,
        is_admin: data.user.is_admin,
        roles: data.user.roles
      });
      
      // Check role match for admin login
      if (role === "admin" && data.user.role !== "admin") {
        console.log("[AUTH] Admin check failed - clearing session");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        throw new Error("This account does not have admin privileges");
      }
      
      // Show subscription warning if applicable
      if (data.user.subscription_message) {
        toast.warning(data.user.subscription_message, { duration: 5000 });
      }
      
      // Show password change prompt if needed
      if (data.user.force_password_change) {
        toast.info("Please change your password after login", { duration: 5000 });
      }
      
      toast.success("Welcome back!");
      
      // Navigate based on role - SINGLE ROLE MODEL
      const userRole = data.user.role;
      console.log("[AUTH] Redirect decision: role=", userRole);
      
      if (userRole === "admin") {
        console.log("[AUTH] Redirecting to /admin/dashboard");
        navigate("/admin/dashboard");
      } else if (userRole === "trainer") {
        console.log("[AUTH] Redirecting to /dashboard/trainer");
        navigate("/dashboard/trainer");
      } else if (userRole === "enterprise") {
        console.log("[AUTH] Redirecting to /dashboard/enterprise");
        navigate("/dashboard/enterprise");
      } else {
        // Default: talent
        console.log("[AUTH] Redirecting to /dashboard/talent");
        navigate("/dashboard/talent");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplay = () => {
    if (role === "admin") return "Administrator";
    if (role === "trainer") return "Trainer";
    if (role === "enterprise") return "Enterprise";
    return "Trainer / Enterprise";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
            <span className="font-display text-xl font-bold text-gray-900">TalentMeet</span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="text-center mb-8">
              {role === "admin" && (
                <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-teal-600" />
                </div>
              )}
              <h1 className="font-display text-2xl font-bold text-gray-900 mb-2">
                {role === "admin" ? "Admin Login" : "Welcome Back"}
              </h1>
              <p className="text-gray-600">
                Sign in as <span className="font-semibold capitalize text-violet-600">{getRoleDisplay()}</span>
              </p>
            </div>

            {/* Email/Password Form - For ALL roles (Talent, Trainer, Enterprise, Admin) */}
            {/* Admin form uses autoComplete="off" to prevent browser autofill for security */}
            <form 
              onSubmit={handleSubmit} 
              className="space-y-4"
              autoComplete={isAdminRoute ? "off" : "on"}
            >
              <div>
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 h-12"
                    required
                    data-testid="email-input"
                    autoComplete={isAdminRoute ? "new-password" : "email"}
                    name={isAdminRoute ? `admin-email-${Date.now()}` : "email"}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 h-12"
                    required
                    data-testid="password-input"
                    autoComplete={isAdminRoute ? "new-password" : "current-password"}
                    name={isAdminRoute ? `admin-pass-${Date.now()}` : "password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox - 7-day persistent session */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="remember-me" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    data-testid="remember-me-checkbox"
                  />
                  <Label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer">
                    Remember me for 7 days
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                className={`w-full h-12 ${role === "admin" ? "bg-teal-600 hover:bg-teal-700" : "btn-primary"}`}
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : (role === "admin" ? "Sign In as Admin" : "Sign In")}
              </Button>
              
              {/* Forgot Password - Admin Only Reset */}
              <p className="text-center text-gray-500 text-xs mt-3">
                Forgot password?{" "}
                <button
                  type="button"
                  onClick={() => toast.info("Password reset is handled by support. Please contact support via WhatsApp for assistance.", { duration: 5000 })}
                  className="text-violet-600 hover:text-violet-700 font-medium"
                >
                  Contact Support
                </button>
              </p>
            </form>

            {role !== "admin" && (
              <p className="text-center text-gray-600 text-sm mt-6">
                Don&apos;t have an account?{" "}
                <Link 
                  to="/register" 
                  state={{ role }} 
                  className="text-violet-600 hover:text-violet-700 font-medium"
                  data-testid="register-link"
                >
                  Create one
                </Link>
              </p>
            )}

            {role === "admin" && (
              <p className="text-center text-gray-500 text-xs mt-6">
                Admin access is restricted. Contact support if you need assistance.
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
